import { Logger } from '@deqo/foundation';
import { Component, ObjectManager } from '../../../concepts/objects';
import { Matrix } from '../../../platform/geom/Matrix';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { ActiveObject, resolve } from '../../Container';
import { Camera, CameraState } from '../Camera';
import { Physical, RelativeTo } from '../entities/Physical';
import { Graffiti, GraffitiAttr, GraffitiTarget } from './Graffiti';

const errLog = Logger.get('app.errors');
const metLog = Logger.get('app.metrics');

const lodForScale = (scale: number) => {
    if (scale <= .25) return .25;
    if (scale <= .5) return .5;
    if (scale <= 1) return 1;
    if (scale <= 2) return 2;
    if (scale <= 4) return 4;
};

const lodMaxForSize = (size: number) => {
    if (size >= 32768) return .25;
    if (size >= 16384) return .5;
    if (size >= 8192) return 1;
    if (size >= 4096) return 2;
    return 4;
};

interface GraffitiCanvas extends HTMLCanvasElement {
    baseWidth: number;
    baseHeight: number;
}

export class GraffitiMechanics extends ActiveObject {

    @resolve(Camera) camera!: Camera;
    @resolve(ObjectManager) objects!: ObjectManager;

    private ready = false;
    private lodSetting = null;
    private canvases = new Map<string, GraffitiCanvas>();
    private tracker = new Map<Graffiti, Physical>();
    private invalidations = new Set<Graffiti>();
    private repaintRequested = false;
    private repaintTargets = new Set<Physical>();

    public start(): void {
        this.burden(
            this.camera.observe(this.lodSwapWhenAppropriate.bind(this)),
            this.objects.on('component', 'allocated', this.invalidatePotentialGraffiti.bind(this)),
            this.objects.on('component', 'released', this.invalidatePotentialGraffiti.bind(this)),
            this.objects.on('component', 'affected', this.invalidatePotentialGraffitiTargetOnResize.bind(this)),
            this.objects.on('updated', this.refresh.bind(this)),
        );
        this.init();
    }

    private async init() {
        await this.camera.waitForCalibrated();
        this.lodSetting = lodForScale(this.camera.scale);
        this.ready = true;
        this.objects.getAll(Graffiti).forEach(x => this.invalidations.add(x));
        this.refresh();
    }

    private lodSwapWhenAppropriate(cs: CameraState) {
        if (!this.ready) return;
        const lod = lodForScale(cs.scale);
        if (this.lodSetting !== lod) {
            this.lodSetting = lod;
            this.scheduler.windowed('lodSwap', 1000, () => this.scheduleRepaintAll());
        }
    }

    private invalidatePotentialGraffiti([x]: [Component]) {
        // When a graffiti is allocated, released or affected, mark it as invalidated
        if (x.is(Graffiti)) {
            this.invalidations.add(x as Graffiti);
        }
    }

    private invalidatePotentialGraffitiTargetOnResize([x]: [Component]) {
        // When a graffiti target changes size we should repaint
        const gt = x.as(GraffitiTarget);
        if (!gt?.alive) return;
        const gts = gt.size();
        const gc = this.canvases.get(x.id);
        if (!gc) return;
        if (gc.baseWidth === gts.w && gc.baseHeight === gts.h) return;
        this.repaintTargets.add(gt);
        this.scheduleRepaint();
    }

    private refresh() {
        if (!this.ready || !this.invalidations.size) return;
        for (const x of this.invalidations) {
            // For any invalidated graffiti, if they are alive and mounted, make sure we are tracking their parent
            if (x.alive && !!x.parent?.is(Physical)) {
                this.tracker.set(x, x.parent as Physical);
            }
            // Add parent to redraws (use tracker because when released the object won't be mounted anymore so we won't know the parent)
            const parent = this.tracker.get(x);
            if (parent?.alive) {
                this.repaintTargets.add(parent);
            }
            // If dead, remove from tracker
            if (!x.alive) {
                this.tracker.delete(x);
            }
        }
        this.invalidations.clear();
        this.scheduleRepaint();
    }

    private scheduleRepaint() {
        if (!this.repaintRequested && !!this.repaintTargets.size) {
            this.repaintRequested = true;
            requestAnimationFrame(this.repaint.bind(this));
        }
    }

    private scheduleRepaintAll() {
        for (const gt of this.tracker.values()) {
            this.repaintTargets.add(gt);
        }
        this.scheduleRepaint();
    }

    // private doRepaint() ReactDOM.unstable_batchedUpdates(() => {
    private repaint() {
        const ts = performance.now();
        this.repaintRequested = false;
        const renderQueue = [] as Physical[];
        for (const gt of this.repaintTargets) {
            if (gt.alive && gt.is(Physical) && !!gt.children.find(x => !!x.is(Graffiti))) {
                renderQueue.push(gt);
            }
            else {
                this.releaseCanvas(gt);
                this.repaintTargets.delete(gt);
            }
        }

        for (const gt of renderQueue) {
            this.updateCanvas(gt);
            this.repaintTargets.delete(gt);
            const te = performance.now() - ts;
            if (te > 16) break;
        }

        this.scheduleRepaint();
    }

    private getOrAllocateCanvas(target: Physical) {
        let inst = this.canvases.get(target.id);
        if (!inst) {
            inst = document.createElement('canvas') as GraffitiCanvas;
            GraffitiAttr.set(target, inst);
            this.canvases.set(target.id, inst);
        }
        return inst;
    }

    private updateCanvas(target: Physical) {
        const t = performance.now();
        try {
            const ts = target.size();
            const lodMax = lodMaxForSize(Math.max(ts.w, ts.h));
            const lodMin = target.mounted() ? .5 : 1;
            const lod = Math.max(Math.min(this.lodSetting, lodMax), lodMin);

            const canvas = this.getOrAllocateCanvas(target);
            canvas.baseWidth = ts.w;
            canvas.baseHeight = ts.h;
            canvas.width = ts.w * lod;
            canvas.height = ts.h * lod;

            const gfx = UniversalSurfaceRenderer.from(canvas);
            gfx.clearRect(0, 0, canvas.width, canvas.height);
            // gfx.font = '80px Ubuntu';
            // gfx.fillText(`${canvas.width}x${canvas.width}`, 25, 80);

            try {
                for (const tc of target.children) {
                    if (!tc.alive) continue;
                    const tg = tc.as(Graffiti);
                    if (tg) {
                        gfx.setTransform(Matrix.scale(lod, lod));
                        tg.paintTo(gfx);
                    }
                }
            }
            catch (e) {
                errLog.error(e);
            }

            const mst = target.transform(RelativeTo.Model).inverse();
            const clip = target.path(RelativeTo.Model).transform(mst).toPath2D();

            gfx.setTransform(Matrix.scale(lod, lod));
            gfx.globalCompositeOperation = 'destination-in';
            gfx.fillStyle = '#000';
            gfx.fill(clip);
        }
        finally {
            metLog.log('graffitiPaint', performance.now() - t, 'ms');
        }
    }

    private releaseCanvas(target: Physical) {
        if (target.alive) {
            GraffitiAttr.set(target, null);
        }
        this.canvases.delete(target.id);
    }
}
